import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Calendar, Users, Cat, Receipt, Clock, AlertTriangle, Bell, UserPlus, XCircle, UserX, CreditCard, Euro, Camera, Upload, X, Loader2 } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { NotificationCarousel } from '../components/NotificationCarousel';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Dashboard = () => {
  const { user, isInstructor, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentCours, setRecentCours] = useState([]);
  const [mesChevaux, setMesChevaux] = useState([]);
  const [mesGroupes, setMesGroupes] = useState([]);
  const [absencesData, setAbsencesData] = useState(null);
  
  // Photo du jour modal
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);
  
  // Auto-open photo modal if redirected from gallery
  useEffect(() => {
    if (searchParams.get('openPhotoModal') === 'true') {
      setPhotoModalOpen(true);
      // Clean up URL
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);
  
  // Handle file selection
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      const wrappedFiles = files.map((file, idx) => ({
        id: `photo_${Date.now()}_${idx}`,
        file,
        previewUrl: URL.createObjectURL(file)
      }));
      setSelectedFiles(prev => [...prev, ...wrappedFiles]);
    }
  };
  
  // Remove a file
  const removeFile = (fileId) => {
    setSelectedFiles(prev => {
      const file = prev.find(f => f.id === fileId);
      if (file?.previewUrl) URL.revokeObjectURL(file.previewUrl);
      return prev.filter(f => f.id !== fileId);
    });
  };
  
  // Upload photos with "Photo du jour" tag
  const uploadPhotoDuJour = async () => {
    if (selectedFiles.length === 0) return;
    
    setUploading(true);
    setUploadProgress(0);
    
    const today = new Date().toLocaleDateString('fr-FR', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
    
    let successCount = 0;
    
    for (let i = 0; i < selectedFiles.length; i++) {
      const { file } = selectedFiles[i];
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', `Photo du jour - ${today}`);
      formData.append('custom_tags', 'photo_du_jour');
      
      try {
        await axios.post(`${API_URL}/api/gallery/upload/fast`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            const fileProgress = (progressEvent.loaded / progressEvent.total) * 100;
            const overallProgress = ((i + fileProgress / 100) / selectedFiles.length) * 100;
            setUploadProgress(Math.round(overallProgress));
          }
        });
        successCount++;
      } catch (error) {
        console.error(`Error uploading ${file.name}:`, error);
        toast.error(`Erreur: ${file.name}`);
      }
    }
    
    setUploading(false);
    
    if (successCount > 0) {
      toast.success(`${successCount} photo(s) du jour ajoutée(s) !`);
      // Clean up
      selectedFiles.forEach(f => {
        if (f.previewUrl) URL.revokeObjectURL(f.previewUrl);
      });
      setSelectedFiles([]);
      setPhotoModalOpen(false);
    }
  };

  const fetchDashboardData = async () => {
    try {
      if (isInstructor()) {
        // Toutes les requêtes instructeur en parallèle
        const [statsRes, coursRes, absencesRes] = await Promise.all([
          axios.get(`${API_URL}/api/stats/dashboard`),
          axios.get(`${API_URL}/api/mes-cours-instructeur`).catch(() => null),
          axios.get(`${API_URL}/api/cours-avec-absences`).catch(() => null),
        ]);
        setStats(statsRes.data);
        setRecentCours(coursRes?.data?.slice(0, 5) || []);
        if (absencesRes) setAbsencesData(absencesRes.data);
      } else {
        // Toutes les requêtes cavalier en parallèle
        const [statsRes, coursRes, chevauxRes, groupesRes] = await Promise.all([
          axios.get(`${API_URL}/api/stats/dashboard`),
          axios.get(`${API_URL}/api/mes-cours`).catch(() => null),
          axios.get(`${API_URL}/api/horses/my-horses`).catch(() => null),
          axios.get(`${API_URL}/api/mes-groupes`).catch(() => null),
        ]);
        setStats(statsRes.data);
        setRecentCours(coursRes?.data || []);
        setMesChevaux(chevauxRes?.data || []);
        setMesGroupes(groupesRes?.data || []);
      }
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const InstructorDashboard = () => (
    <>
      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Card 
          className="card-hover cursor-pointer transition-transform hover:scale-[1.02]" 
          onClick={() => navigate('/planning')}
          data-testid="card-cours-today"
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-stone-500">Cours aujourd'hui</p>
                <p className="text-2xl font-bold text-[#2E4A3D]">{stats?.cours_aujourd_hui || 0}</p>
              </div>
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="card-hover cursor-pointer transition-transform hover:scale-[1.02]" 
          onClick={() => navigate('/users')}
          data-testid="card-cavaliers"
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-stone-500">Cavaliers actifs</p>
                <p className="text-2xl font-bold text-[#2E4A3D]">{stats?.cavaliers_actifs || 0}</p>
              </div>
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="card-hover cursor-pointer transition-transform hover:scale-[1.02]" 
          onClick={() => navigate('/horses')}
          data-testid="card-chevaux"
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-stone-500">Chevaux</p>
                <p className="text-2xl font-bold text-[#2E4A3D]">{stats?.chevaux_actifs || 0}</p>
              </div>
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <Cat className="w-5 h-5 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="card-hover cursor-pointer transition-transform hover:scale-[1.02]" 
          onClick={() => navigate('/planning?filter=absences')}
          data-testid="card-absences-futures"
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-stone-500">Absences futures</p>
                <p className="text-2xl font-bold text-[#E07A5F]">{absencesData?.total_absences || 0}</p>
              </div>
              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                <UserX className="w-5 h-5 text-orange-600" />
              </div>
            </div>
            {absencesData?.mes_cours_count > 0 && (
              <p className="text-xs text-stone-500 mt-2">
                {absencesData.mes_cours_count} cours concerné{absencesData.mes_cours_count > 1 ? 's' : ''}
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Alerts & Quick Actions */}
      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        {/* Pending approvals */}
        {isAdmin() && stats?.demandes_en_attente > 0 && (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-4">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-amber-800">Demandes en attente</h3>
                  <p className="text-sm text-amber-700 mt-1">
                    {stats.demandes_en_attente} demande(s) d'inscription en attente de validation
                  </p>
                  <Button 
                    size="sm" 
                    className="mt-3 bg-amber-600 hover:bg-amber-700 text-white"
                    onClick={() => navigate('/users?filter=pending')}
                    data-testid="view-pending-btn"
                  >
                    Voir les demandes
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick actions */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold">Actions rapides</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-4">
            <Button 
              variant="outline" 
              className="h-auto py-6 flex flex-col gap-3 border-2 border-amber-400 bg-gradient-to-br from-amber-50 to-amber-100 hover:from-amber-100 hover:to-amber-200 text-amber-700 shadow-sm hover:shadow-md transition-all"
              onClick={() => setPhotoModalOpen(true)}
              data-testid="quick-photo-btn"
            >
              <div className="w-12 h-12 rounded-full bg-amber-200 flex items-center justify-center">
                <Camera className="w-6 h-6 text-amber-700" />
              </div>
              <span className="text-sm font-medium">Photo du jour</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-auto py-6 flex flex-col gap-3 border-2 border-emerald-300 bg-gradient-to-br from-emerald-50 to-emerald-100 hover:from-emerald-100 hover:to-emerald-200 text-emerald-700 shadow-sm hover:shadow-md transition-all"
              onClick={() => navigate('/planning')}
              data-testid="quick-planning-btn"
            >
              <div className="w-12 h-12 rounded-full bg-emerald-200 flex items-center justify-center">
                <Calendar className="w-6 h-6 text-emerald-700" />
              </div>
              <span className="text-sm font-medium">Nouveau cours</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-auto py-6 flex flex-col gap-3 border-2 border-sky-300 bg-gradient-to-br from-sky-50 to-sky-100 hover:from-sky-100 hover:to-sky-200 text-sky-700 shadow-sm hover:shadow-md transition-all"
              onClick={() => navigate('/horses')}
              data-testid="quick-horse-btn"
            >
              <div className="w-12 h-12 rounded-full bg-sky-200 flex items-center justify-center">
                <Cat className="w-6 h-6 text-sky-700" />
              </div>
              <span className="text-sm font-medium">Ajouter cheval</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-auto py-6 flex flex-col gap-3 border-2 border-violet-300 bg-gradient-to-br from-violet-50 to-violet-100 hover:from-violet-100 hover:to-violet-200 text-violet-700 shadow-sm hover:shadow-md transition-all"
              onClick={() => navigate('/billing')}
              data-testid="quick-billing-btn"
            >
              <div className="w-12 h-12 rounded-full bg-violet-200 flex items-center justify-center">
                <Receipt className="w-6 h-6 text-violet-700" />
              </div>
              <span className="text-sm font-medium">Facturation</span>
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Upcoming courses */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Mes prochains cours</CardTitle>
            <Button variant="link" onClick={() => navigate('/planning')} className="text-[#2E4A3D]">
              Voir tout
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {recentCours.length === 0 ? (
            <div className="text-center py-8 text-stone-500">
              <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Aucun cours programmé</p>
            </div>
          ) : (
            <div className="space-y-3">
              {recentCours.map((cours) => (
                <div 
                  key={cours.id} 
                  className="flex items-center justify-between p-3 rounded-lg bg-stone-50 hover:bg-stone-100 transition-colors cursor-pointer"
                  onClick={() => navigate(`/planning?cours=${cours.id}`)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#4C1D95]" />
                    <div>
                      <p className="font-medium text-sm">{(cours.type_cours || cours.type_activite || 'Cours').replace('_', ' ')}</p>
                      <p className="text-xs text-stone-500">
                        {new Date(cours.date_heure_debut).toLocaleDateString('fr-FR', {
                          weekday: 'short',
                          day: 'numeric',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                  <Badge variant="outline">
                    {cours.places_disponibles}/{cours.places_max}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );

  const CavalierDashboard = () => (
    <>
      {/* Notifications carousel - tout en haut */}
      <div className="mb-6">
        <NotificationCarousel />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        <Card 
          className="card-hover cursor-pointer transition-transform hover:scale-[1.02]"
          onClick={() => navigate('/planning?filter=mes-cours')}
          data-testid="card-mes-cours"
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-stone-500">Mes cours</p>
                <p className="text-2xl font-bold text-[#2E4A3D]">{stats?.mes_cours || 0}</p>
              </div>
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="card-hover cursor-pointer transition-transform hover:scale-[1.02]"
          onClick={() => navigate('/billing')}
          data-testid="card-facturation"
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-stone-500">Facturation</p>
                <div className="flex items-baseline gap-2">
                  <p className="text-2xl font-bold text-[#E07A5F]">{stats?.montant_a_payer || 0}€</p>
                  {(stats?.factures_en_attente || 0) > 0 && (
                    <span className="text-sm text-stone-500">
                      ({stats.factures_en_attente} facture{stats.factures_en_attente > 1 ? 's' : ''})
                    </span>
                  )}
                </div>
              </div>
              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                <Receipt className="w-5 h-5 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {mesChevaux.length > 0 && (
          <Card 
            className="card-hover cursor-pointer transition-transform hover:scale-[1.02]"
            onClick={() => navigate('/horses')}
            data-testid="card-mes-chevaux"
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-stone-500">{mesChevaux.length === 1 ? 'Mon cheval' : 'Mes chevaux'}</p>
                  <p className="text-2xl font-bold text-[#2E4A3D]">{mesChevaux.length}</p>
                </div>
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <Cat className="w-5 h-5 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Quick actions for cavaliers */}
      <Card className="mb-6">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Actions rapides</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-3 gap-3">
          <Button 
            className="btn-primary h-auto py-4 flex flex-col gap-2"
            onClick={() => navigate('/planning?action=inscription')}
            data-testid="cavalier-inscription-btn"
          >
            <UserPlus className="w-5 h-5" />
            <span className="text-xs">S'inscrire</span>
          </Button>
          <Button 
            variant="outline"
            className="h-auto py-4 flex flex-col gap-2 border-amber-300 text-amber-700 hover:bg-amber-50"
            onClick={() => navigate('/planning?action=absence')}
            data-testid="cavalier-absence-btn"
          >
            <XCircle className="w-5 h-5" />
            <span className="text-xs">Déclarer absence</span>
          </Button>
          <Button 
            variant="outline"
            className="h-auto py-4 flex flex-col gap-2"
            onClick={() => navigate('/billing')}
            data-testid="cavalier-payer-btn"
          >
            <CreditCard className="w-5 h-5" />
            <span className="text-xs">Mes factures</span>
          </Button>
        </CardContent>
      </Card>

      {/* My groups section */}
      {mesGroupes.length > 0 && (
        <Card className="mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              {mesGroupes.length === 1 ? 'Mon groupe' : 'Mes groupes'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {mesGroupes.map((groupe) => (
                <Badge 
                  key={groupe.id} 
                  variant="outline"
                  className="px-3 py-2 text-sm border-blue-200 bg-blue-50 text-blue-700"
                >
                  <Users className="w-4 h-4 mr-2" />
                  {groupe.nom}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Upcoming courses - filtered for this cavalier */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Mes prochains cours</CardTitle>
            <Button variant="link" onClick={() => navigate('/planning?filter=mes-cours')} className="text-[#2E4A3D]">
              Voir tout
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {recentCours.length === 0 ? (
            <div className="text-center py-8 text-stone-500">
              <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Aucun cours programmé</p>
              <Button 
                variant="link" 
                onClick={() => navigate('/planning?action=inscription')}
                className="mt-2 text-[#2E4A3D]"
              >
                S'inscrire à un cours
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {recentCours.slice(0, 3).map((cours) => (
                <div 
                  key={cours.id} 
                  className="flex items-center justify-between p-3 rounded-lg bg-stone-50 hover:bg-stone-100 transition-colors cursor-pointer"
                  onClick={() => navigate(`/planning?cours=${cours.id}`)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#4C1D95]" />
                    <div>
                      <p className="font-medium text-sm">{(cours.type_cours || cours.type_activite || 'Cours').replace('_', ' ')}</p>
                      <p className="text-xs text-stone-500">
                        {new Date(cours.date_heure_debut).toLocaleDateString('fr-FR', {
                          weekday: 'short',
                          day: 'numeric',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                  <Badge variant="outline">
                    {cours.places_disponibles}/{cours.places_max}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="animate-fadeIn" data-testid="dashboard">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-serif font-bold text-[#2E4A3D]">
          {isInstructor() ? 'Tableau de bord' : `Bienvenue, ${user?.prenom}`}
        </h1>
        <p className="text-stone-500 mt-1">
          {new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
        </p>
      </div>

      {isInstructor() ? <InstructorDashboard /> : <CavalierDashboard />}
      
      {/* Photo du jour Modal */}
      <Dialog open={photoModalOpen} onOpenChange={setPhotoModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5 text-amber-500" />
              Photo du jour
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-stone-500">
              Ajoutez rapidement des photos qui seront automatiquement taguées "Photo du jour" pour un classement ultérieur.
            </p>
            
            {/* File input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,video/*"
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />
            
            {/* Drop zone */}
            <div 
              className="border-2 border-dashed border-stone-300 rounded-lg p-6 text-center hover:border-amber-400 transition-colors cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="w-8 h-8 text-stone-400 mx-auto mb-2" />
              <p className="text-sm text-stone-600">Cliquez pour sélectionner des photos</p>
              <p className="text-xs text-stone-400 mt-1">ou glissez-déposez vos fichiers ici</p>
            </div>
            
            {/* Selected files preview */}
            {selectedFiles.length > 0 && (
              <div>
                <p className="text-sm font-medium mb-2">{selectedFiles.length} fichier(s) sélectionné(s)</p>
                <div className="grid grid-cols-4 gap-2">
                  {selectedFiles.map((wrappedFile) => (
                    <div key={wrappedFile.id} className="relative aspect-square bg-stone-200 rounded-lg overflow-hidden group">
                      {wrappedFile.file.type.startsWith('image/') ? (
                        <img 
                          src={wrappedFile.previewUrl} 
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <video 
                          src={wrappedFile.previewUrl}
                          className="w-full h-full object-cover"
                          muted
                        />
                      )}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeFile(wrappedFile.id);
                        }}
                        className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Progress bar */}
            {uploading && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin text-amber-500" />
                  <span className="text-sm">Upload en cours... {uploadProgress}%</span>
                </div>
                <div className="h-2 bg-stone-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-amber-500 transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            )}
            
            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  selectedFiles.forEach(f => {
                    if (f.previewUrl) URL.revokeObjectURL(f.previewUrl);
                  });
                  setSelectedFiles([]);
                  setPhotoModalOpen(false);
                }}
                disabled={uploading}
              >
                Annuler
              </Button>
              <Button
                className="flex-1 bg-amber-500 hover:bg-amber-600"
                onClick={uploadPhotoDuJour}
                disabled={selectedFiles.length === 0 || uploading}
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Upload...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Ajouter ({selectedFiles.length})
                  </>
                )}
              </Button>
            </div>
            
            {/* Link to gallery */}
            <button
              type="button"
              onClick={() => {
                setPhotoModalOpen(false);
                navigate('/gallery?tag=photo_du_jour');
              }}
              className="w-full text-center text-sm text-amber-600 hover:underline"
            >
              Voir les photos du jour dans la galerie →
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
